import React from 'react';

import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiCalendar } from '@lumapps/lumx/icons';
import { Text, Typography } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { formatDate } from '@lumapps/utils/date/formatDate';
import { getFormattedTimezone } from '@lumapps/utils/timezones/getFormattedTimezone';

import { DrawerSection, DrawerSectionProps } from '../DrawerSection';

export interface DrawerDateSectionProps extends DrawerSectionProps {
    /** date when the action occurred */
    date: string;
    /** date format */
    format?: string;
    /** scope of the feature */
    scope: string;
    /** time zone, not displayed by default */
    timeZone?: string;
}

/**
 * Displays a date inside the context of a drawer
 * @family Drawer
 * @param DrawerDateSectionProps
 * @returns DrawerDateSection
 */
export const DrawerDateSection: React.FC<DrawerDateSectionProps> = ({ date, scope, format, timeZone, ...props }) => {
    const language = useSelector(currentLanguageSelector);
    const { get } = useDataAttributes(scope);

    return (
        <DrawerSection
            icon={mdiCalendar}
            helper={
                timeZone
                    ? getFormattedTimezone({
                          language,
                          timeZone,
                          withTimeZoneName: true,
                          timeZoneNameFormat: 'long',
                      })
                    : undefined
            }
            textProps={{
                typography: 'body1',
            }}
            {...props}
        >
            <Text as="span" typography={Typography.body1} {...get({ element: 'section', action: 'date' })}>
                {formatDate(date, format)}
            </Text>
        </DrawerSection>
    );
};
